import React, {
    lazy,
    useContext,
    useEffect,
} from "react";
import * as PropTypes from "prop-types";
import {Route as ReactRoute, Redirect} from "react-router-dom";
import {useComposePages} from "../../../hooks/useComposePages";
import {LocaleContext} from "../../../store/providers/LocaleProvider";
import {ResortsContext} from "../../../store/providers/ResortsProvider";
import {ERROR_PAGE_404} from "../../../common/routes";
import LocalizedSwitch from "./LocalizedSwitch";
import {ROLES_MAP} from "../../../common/constant";
import PrivateRoute from "./PrivateRoute";
import Route from "./Route";

const ComposeViewWrapper = lazy(() => import("../../view/compose/ComposeViewWrapper"));
const ResortsView = lazy(() => import("../../view/searchbar/ResortsView"));
const ResortDetailView = lazy(() => import("../../view/resort/ResortDetailView"));
const ResortRouteComponentWrapper = lazy(() => import("../routing/ResortRouteComponentWrapper"));

export const DynamicRouter = ({match}) => {
    const {composePages, getComposePage, composePagesLoading} = useComposePages();
    const {locale, localeUppercaseUnderscore} = useContext(LocaleContext)[0];
    const [resortsStateAndIds, resortsDispatch] = useContext(ResortsContext);
    const {getResortIds, resorts, resortsLoading} = resortsStateAndIds;

    useEffect(() => {
        if (resorts && !resorts.length && !resortsLoading) {
            resortsDispatch({type: "GET_RESORTS_INIT", payload: {locale: localeUppercaseUnderscore, is_bookable: true}});
        }
    }, [resorts]);

    const destinationPaths = [
        {
            path: "/:country",
            component: <ResortsView />
        },
        {
            path: "/:country/:destination",
            component: <ResortsView />
        },
        {
            path: "/:country/:destination/:resort",
            component: <ResortDetailView />
        }
    ];

    const getComposePageRoles = (page) => {
        let composePageRoles = [];
        const visibleForUserRoles = page?.visibleForUserRoles;

        if(visibleForUserRoles) {
            const userRoles = visibleForUserRoles.map((role) => ROLES_MAP[role]);
            // these roles are OR, so we need to create array with arrays of each role for use in PrivateRoute
            const pageRoles = [];
            userRoles.forEach(roleGroup => {
                roleGroup?.forEach(role => {
                    pageRoles.push([role]);
                });
            });

            if(pageRoles.length > 0) {
                composePageRoles = pageRoles;
            }
        }
        return composePageRoles;
    };

    return (
        <>
            {!resortsLoading && resorts && !composePagesLoading && composePages &&
                <>
                    {getComposePage(match.params[0]) ? (() => {
                        const composePage = getComposePage(match.params[0]);
                        const composePageRoles = getComposePageRoles(composePage);
                        return (
                            <>
                                {composePageRoles?.length > 0 ? (
                                    <PrivateRoute
                                        roles={composePageRoles}
                                        path={"*"}
                                        type={composePage.slug}
                                    >
                                        <ComposeViewWrapper
                                            slug={composePage[`slug_${locale.replace("-", "_")}`]}
                                        />
                                    </PrivateRoute>
                                ) : (
                                    <Route
                                        path={"*"}
                                        type={composePage.slug}
                                    >
                                        <ComposeViewWrapper
                                            slug={composePage[`slug_${locale.replace("-", "_")}`]}
                                        />
                                    </Route>
                                )}
                            </>
                        );
                    })() : (
                        <LocalizedSwitch>
                            {destinationPaths.map((route, index) => {
                                return (
                                    <ReactRoute
                                        exact
                                        key={index}
                                        path={route?.path}
                                        render={({match}) => {
                                            const params = match ? match.params : {};
                                            const resortIds = getResortIds(resorts, params?.country, params?.destination);

                                            return (
                                                <ResortRouteComponentWrapper
                                                    params={params}
                                                    resortIds={resortIds}
                                                    resorts={resorts}
                                                    redirectRoute={ERROR_PAGE_404}
                                                >
                                                    {route?.component}
                                                </ResortRouteComponentWrapper>
                                            );

                                        }}
                                    />
                                );
                            })}

                            {/* Redirect to 404 if no route matched */}
                            <Route path={"*"}>
                                <Redirect to={ERROR_PAGE_404} />
                            </Route>

                        </LocalizedSwitch>
                    )}
                </>
            }
        </>
    );
};

DynamicRouter.propTypes = {
    match: PropTypes.any,
};


export default DynamicRouter;
